import { ANCHOR_TOP_GAP_PX } from './hooks/use-last-turn-spacer'

/**
 * Rounding tolerance for the pin's own writes: the values it compares are
 * never exactly equal after a `scrollTop` write and a subsequent read, so a
 * bare `!==`/`<`/`>` would treat the pin's last write as a takeover.
 */
export const PIN_ROUNDING_TOLERANCE_PX = 2

/**
 * How far behind the pin's own moving target counts as reading ahead rather
 * than still catching up to it. Intentionally distinct from
 * `AUTO_SCROLL_ENGAGE_DISTANCE_PX` (scroll-intent.ts): that constant measures
 * distance from the *bottom edge* to re-engage auto-follow after the user
 * scrolled away from it. This one measures distance from the carry pin's own
 * target, which tracks the send anchor and can sit well above the bottom
 * edge while a turn is still short.
 */
export const PIN_CATCH_UP_TOLERANCE_PX = 50

export interface CarryPinOptions {
  /** The last turn's filler element — observed so the pin rides its animation. */
  spacerEl: HTMLElement | null
}

/**
 * The carry pin: NO auto scroll on chat. The pin's ceiling is the send
 * anchor (the last user message at the top edge), never the growing bottom.
 * While the spacer keeps scrollHeight constant the two are equal and
 * re-pinning each resize frame is what carries the sent message up its
 * ease; once the turn outgrows the viewport the anchor holds still, so
 * streaming and the post-stream reveal grow below the fold without ever
 * moving the view.
 *
 * User takeover, both directions: scrollTop above the last pinned value and
 * away from the target is the user scrolling up (a layout shrink clamps
 * instead); scrollTop below it is the user reading ahead — growth can only
 * raise scrollHeight, never push scrollTop down. Skip the write — never yank
 * them — and let the caller's own scroll measurement disengage auto-follow
 * and stop the pin.
 *
 * Pure and imperative so it can be unit-driven independently of the
 * scroller's render cycle. Returns a teardown that disconnects the observer.
 */
export function startCarryPin(
  scrollElement: HTMLElement,
  getAnchorEl: () => HTMLElement | null,
  { spacerEl }: CarryPinOptions,
): () => void {
  let lastPinnedTop: number | null = null

  const pin = () => {
    const bottomTarget = scrollElement.scrollHeight - scrollElement.clientHeight
    let target = bottomTarget
    const anchorEl = getAnchorEl()
    if (anchorEl) {
      const anchorTop =
        scrollElement.scrollTop +
        anchorEl.getBoundingClientRect().top -
        scrollElement.getBoundingClientRect().top -
        ANCHOR_TOP_GAP_PX
      target = Math.min(bottomTarget, Math.max(0, Math.round(anchorTop)))
    }
    if (lastPinnedTop === null && scrollElement.scrollTop > target + PIN_ROUNDING_TOLERANCE_PX) {
      // Armed while the user already sits below the anchor (an edge jump to
      // the bottom, follow re-engaged near it) — adopt, never yank up.
      lastPinnedTop = target
      return
    }
    const scrolledAbovePin =
      lastPinnedTop !== null && scrollElement.scrollTop < lastPinnedTop - PIN_ROUNDING_TOLERANCE_PX
    if (scrolledAbovePin && target - scrollElement.scrollTop > PIN_CATCH_UP_TOLERANCE_PX) {
      return
    }
    if (lastPinnedTop !== null && scrollElement.scrollTop > lastPinnedTop + PIN_ROUNDING_TOLERANCE_PX) {
      return
    }
    if (scrollElement.scrollTop !== target) {
      scrollElement.scrollTop = target
    }
    lastPinnedTop = target
  }

  const observer = new ResizeObserver(pin)
  observer.observe(scrollElement)
  if (scrollElement.firstElementChild) {
    observer.observe(scrollElement.firstElementChild)
  }
  // The spacer too, so the pin rides its animation: growing it is what raises
  // the anchor target, and re-pinning on each of those frames is what
  // carries the newest turn up to the top edge.
  if (spacerEl) {
    observer.observe(spacerEl)
  }
  pin()
  return () => observer.disconnect()
}
